import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useLazyQuery, useMutation } from '@apollo/client'

import { isEmpty } from 'lodash'
import {
  EXPORT_TRAINING_DATA,
  FIND_ALL_AI_TRAINING_DATA,
  REMOVE_AI_TEST_TRAINING_DATA,
  UPDATE_AI_TEST_TRAINING_DATA,
} from 'queries/trainingData'
import {
  findAllAiTestTrainingData,
  findAllAiTestTrainingDataVariables,
  findAllAiTestTrainingData_findAllAiTestTrainingData_items,
} from '__generatedTypes__/findAllAiTestTrainingData'
import { AITestTypeEnum, OrderDirection, SearchTypeEnum } from '__generatedTypes__/globalTypes'
import { toast } from 'utils/toast'
import {
  updateAiTestTrainingData,
  updateAiTestTrainingDataVariables,
} from '__generatedTypes__/updateAiTestTrainingData'
import {
  removeAiTestTrainingData,
  removeAiTestTrainingDataVariables,
} from '__generatedTypes__/removeAiTestTrainingData'
import { exportTrainingData, exportTrainingDataVariables } from '__generatedTypes__/exportTrainingData'
import { saveBase64AsWordFile } from 'utils/saveFile'
import { FormValues } from './Form'

const TAKE = 10

const useItems = (isFromAI: boolean) => {
  const { id: questionId = '' } = useParams<{ id: string }>()

  const [fetch, { loading, refetch, called }] = useLazyQuery<
    findAllAiTestTrainingData,
    findAllAiTestTrainingDataVariables
  >(FIND_ALL_AI_TRAINING_DATA, {
    variables: {
      query: {
        where: { question: { id: questionId }, isFromAI },
        order: { createdAt: OrderDirection.DESC },
        take: TAKE,
      },
    },
    fetchPolicy: 'network-only',
  })

  const [items, setItems] = useState<findAllAiTestTrainingData_findAllAiTestTrainingData_items[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const page = useRef(0)

  const loadInitialItems = async () => {
    try {
      const { data } = await fetch({
        variables: {
          query: {
            skip: 0,
            take: TAKE,
            where: { question: { id: questionId }, isFromAI },
          },
        },
      })
      page.current = 1
      setItems(data?.findAllAiTestTrainingData.items || [])
      setTotalItems(data?.findAllAiTestTrainingData.count || 0)
    } catch (error) {
      console.error(`Error while fetching initial training data`, error)
    }
  }

  const loadMore = async () => {
    const skip = page.current * TAKE

    if (totalItems == null || totalItems < skip) return

    try {
      const { data } = await fetch({
        variables: {
          query: {
            skip,
            take: TAKE,
            where: { question: { id: questionId }, isFromAI },
          },
        },
      })
      page.current += 1
      setItems([...items, ...(data?.findAllAiTestTrainingData.items || [])])
      setTotalItems(data?.findAllAiTestTrainingData.count || 0)
    } catch (error) {
      console.error(`Error while fetching more training data`, error)
    }
  }

  const search = async (text?: string, email?: string) => {
    try {
      const getSearchFilter = () => {
        if (text) {
          return {
            search: {
              query: text,
              type: SearchTypeEnum.ILike,
            },
          }
        }

        if (email) {
          return {
            search: {
              query: email,
            },
          }
        }

        return {}
      }

      const { data } = await fetch({
        variables: {
          query: {
            skip: 0,
            take: TAKE,
            ...getSearchFilter(),
            where: {
              question: { id: questionId },
              isFromAI,
            },
          },
        },
      })
      page.current = 1
      setItems(data?.findAllAiTestTrainingData.items || [])
      setTotalItems(data?.findAllAiTestTrainingData.count || 0)
    } catch (error) {
      console.error(`Error while searching training data`, error)
    }
  }

  const handleRefetch = async () => {
    const { data } = await refetch()
    if (data?.findAllAiTestTrainingData) {
      setItems(data?.findAllAiTestTrainingData.items || [])
      setTotalItems(data?.findAllAiTestTrainingData.count || 0)
    }
  }

  const handleDelete = async (id: string) => {
    setTotalItems((total) => total - 1)
    setItems((items) => {
      const updatedItems = items.filter((item) => item.id !== id)

      if (updatedItems.length < TAKE) {
        loadInitialItems()
      }

      return updatedItems
    })
  }

  useEffect(() => {
    loadInitialItems()
  }, [])

  return {
    items,
    loading,
    called,
    hasMore: !isEmpty(items) && items.length < totalItems,
    refetch: handleRefetch,
    onDelete: handleDelete,
    loadMore,
    search,
  }
}

export const useGradeEdit = (isFromAI: boolean) => {
  const navigate = useNavigate()
  const { id: questionId } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()

  const items = useItems(isFromAI)
  const { items: trainingData, onDelete } = items

  const [updateTrainingData] = useMutation<updateAiTestTrainingData, updateAiTestTrainingDataVariables>(
    UPDATE_AI_TEST_TRAINING_DATA
  )
  const [removeTrainingData] = useMutation<removeAiTestTrainingData, removeAiTestTrainingDataVariables>(
    REMOVE_AI_TEST_TRAINING_DATA
  )
  const [exportTrainingData, { loading: exportingTrainingData }] = useLazyQuery<
    exportTrainingData,
    exportTrainingDataVariables
  >(EXPORT_TRAINING_DATA, {
    fetchPolicy: 'network-only',
  })

  const titleParam = searchParams.get('title')
  const questionParam = searchParams.get('question')
  const typeParam = searchParams.get('type') as AITestTypeEnum | null

  const titlePart = isFromAI ? 'GPT Grade Editor' : 'Training Grade Editor'
  const title = titleParam ? `${titleParam} - ${titlePart}` : titlePart
  // use search param if training data is empty
  const question = trainingData?.[0]?.question?.questionText || questionParam

  useEffect(() => {
    if (!questionId || !typeParam) {
      navigate(-1)
    }
  }, [questionId, typeParam])

  const handleEditSubmit = async (values: FormValues) => {
    if (!questionId) return

    try {
      await updateTrainingData({
        variables: {
          input: {
            id: values.id,
            answerText: values.response,
            quartile: Number(values.quartile.id),
            quartileExplanation: values.quartileExplanation,
            responseFeedback: values.responseFeedback,
          },
        },
      })
      toast.success('Training data updated successfully')
    } catch (e) {
      toast.error('Failed to update training data')
    }
  }

  const handleDelete = async (id?: string) => {
    if (!id) return

    try {
      await removeTrainingData({
        variables: { id },
      })

      onDelete(id)

      toast.success('Training data deleted successfully')
    } catch (e) {
      toast.error('Failed to delete training data')
    }
  }

  const handleExport = async (isFromAi: boolean) => {
    if (!questionId) return

    try {
      const { data } = await exportTrainingData({ variables: { ids: [questionId], isFromAi } })
      if (!data?.exportTrainingData) return

      const fileName = `${titleParam}.docx` || 'training-data.docx'
      saveBase64AsWordFile(data.exportTrainingData, fileName)
      toast.success('Training data exported successfully')
    } catch (error) {
      toast.error('Failed to export training data')
    }
  }

  return {
    questionId,
    exportingTrainingData,
    title,
    question,
    trainingData,
    handleEditSubmit,
    handleExport,
    handleDelete,
    ...items,
  }
}
